import { useMemo, useState } from 'react';
import { palette } from '../../styles/tokens';
import { Card } from '../common/Card';
import { TimePicker } from '../common/TimePicker';
import type { BusinessHour } from '../../types';

interface Props {
  hours: BusinessHour[];
  onSave: (hours: BusinessHour[]) => Promise<void>;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// Display order: Mon-Sun
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

function formatTime12(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

const ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${palette.g100}` };

export function BusinessHoursSection({ hours, onSave }: Props) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<BusinessHour[]>([]);

  const hoursByDay = new Map(hours.map(h => [h.day_of_week, h]));

  const startEdit = () => {
    setForm(DISPLAY_ORDER.map(d => {
      const existing = hoursByDay.get(d);
      return existing ? { ...existing } : { day_of_week: d, is_open: false, open_time: '09:00', close_time: '17:00' };
    }));
    setEditing(true);
  };

  const updateDay = (idx: number, key: keyof BusinessHour, val: unknown) => {
    setForm(f => f.map((h, i) => i === idx ? { ...h, [key]: val } : h));
  };

  const applyToWeekdays = () => {
    const mon = form.find(h => h.day_of_week === 1);
    if (!mon) return;
    setForm(f => f.map(h => (h.day_of_week >= 1 && h.day_of_week <= 5) ? { ...h, is_open: mon.is_open, open_time: mon.open_time, close_time: mon.close_time } : h));
  };

  const errors = useMemo(() => {
    const e = new Map<number, string>();
    for (const h of form) {
      if (h.is_open && h.open_time && h.close_time && h.open_time >= h.close_time) {
        e.set(h.day_of_week, `Close time must be after open time on ${DAY_NAMES[h.day_of_week]}.`);
      }
    }
    return e;
  }, [form]);
  const canSave = errors.size === 0;

  const save = async () => {
    if (!canSave) return;
    await onSave(form);
    setEditing(false);
  };

  if (!editing) {
    return (
      <Card title="Business Hours" actionLabel="Edit" onAction={startEdit}>
        {DISPLAY_ORDER.map((d, i) => {
          const h = hoursByDay.get(d);
          return (
            <div key={d} style={{ ...ROW, borderBottom: i === 6 ? 'none' : ROW.borderBottom }}>
              <div style={{ width: 120, fontSize: 13, color: palette.g500 }}>{DAY_NAMES[d]}</div>
              <div style={{ fontSize: 13.5, fontWeight: 500, color: h?.is_open ? palette.g800 : palette.g400 }}>
                {h?.is_open ? `${formatTime12(h.open_time)} – ${formatTime12(h.close_time)}` : 'Closed'}
              </div>
            </div>
          );
        })}
      </Card>
    );
  }

  return (
    <Card title="Business Hours">
      {form.map((h, idx) => {
        const err = errors.get(h.day_of_week);
        return (
          <div key={h.day_of_week} style={{ ...ROW, borderBottom: idx === 6 ? 'none' : ROW.borderBottom, flexDirection: 'column', alignItems: 'stretch' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 100, fontSize: 13, color: palette.g500 }}>{DAY_NAMES[h.day_of_week]}</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <div onClick={() => updateDay(idx, 'is_open', !h.is_open)} style={{
                  width: 36, height: 20, borderRadius: 10, cursor: 'pointer',
                  background: h.is_open ? palette.accent : palette.g300,
                  padding: 2, transition: 'background 0.15s', display: 'flex', alignItems: 'center',
                }}>
                  <div style={{ width: 16, height: 16, borderRadius: 8, background: palette.white, transition: 'margin-left 0.15s', marginLeft: h.is_open ? 16 : 0 }} />
                </div>
                <span style={{ fontSize: 12, color: palette.g500, width: 40 }}>{h.is_open ? 'Open' : 'Closed'}</span>
              </label>
              {h.is_open && (
                <>
                  <div style={{ width: 130 }}>
                    <TimePicker value={h.open_time || null} onChange={(v) => updateDay(idx, 'open_time', v ?? '09:00')} step={15} clearable={false} error={err ? ' ' : undefined} />
                  </div>
                  <span style={{ fontSize: 12, color: palette.g400 }}>to</span>
                  <div style={{ width: 130 }}>
                    <TimePicker value={h.close_time || null} onChange={(v) => updateDay(idx, 'close_time', v ?? '17:00')} step={15} minTime={h.open_time || undefined} clearable={false} error={err ? ' ' : undefined} />
                  </div>
                </>
              )}
            </div>
            {err && <div style={{ fontSize: 12, color: palette.red, marginTop: 4, marginLeft: 112 }}>{err}</div>}
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16, alignItems: 'center' }}>
        <button onClick={applyToWeekdays} style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 12, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Apply Mon hours to weekdays</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setEditing(false)} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 500, color: palette.g500, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={save} disabled={!canSave} style={{ padding: '7px 20px', borderRadius: 6, border: 'none', background: canSave ? palette.accent : palette.g300, color: palette.white, fontSize: 13, fontWeight: 600, cursor: canSave ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>Save</button>
        </div>
      </div>
    </Card>
  );
}
